/**
 * The feed's "Ask <handle>" (community feed plan §2 "Ask" / "Chip bank", slice 4): which chips a
 * review offers, what a chip tap puts in the field, and the question body "Ask" posts. Pure — the
 * bank is `./ask-chips`, the UI is `components/community/AskOwner.tsx`.
 *
 * A chip is a suggestion (UX review 2026-10-18): a tap puts its text in the field and the reader
 * posts with "Ask". The posted question carries the chip's id (`template_id`) and the review
 * (`note_id`) only while the field holds the chip's unedited text (`askTemplateFor`); edited text
 * posts as a typed question.
 *
 * The three rules, in this order:
 * (a) a chip whose `read` the owner already answered better or worse is not offered (the review's
 *     ✓ / ✕ reads line, `readChips`); an "as expected" read keeps it;
 * (b) a chip already asked on this review (its id in the item's `asked`, DealHunt #206/#208: the
 *     chips asked on this review plus product-wide ones posted with no review) stays in
 *     its place as a dashed link to that question — "· answered" once it has an answer, else
 *     "· asked"; an `asked` id the bank no longer has is ignored;
 * (c) the first three chips left after (a) are shown, asked ones included in the three (mockup
 *     board 1: two chips and an answered one); none left → "Your question…" alone.
 * Free-text questions are never deduplicated.
 */
import { withSlash } from 'lib/dealhunt/url'
import { ASK_CHIP_BANK, type AskChip, type AskChipCategory } from './ask-chips'
import { rootHref } from './discussions'
import type { DiscussionCreateBody } from './discussions-types'
import { communityCategoryPath, readChips } from './model'
import type { CommunityAskedChip, CommunityFeedItem } from './types'

/** Chips shown under the line at most (rule c). */
export const ASK_CHIPS_SHOWN = 3

/** The free-text chip and its field's placeholder. */
export const ASK_FREE_TEXT = 'Your question…'

/** DealHunt's title limits (discussions contract §2): the question is the title, one line. */
export const ASK_TITLE_MIN = 3
export const ASK_TITLE_MAX = 150

/** DealHunt's template id format (#205) — every bank id must match it. */
export const ASK_TEMPLATE_ID_RE = /^[a-z0-9_]+(\.[a-z0-9_]+)+$/
export const ASK_TEMPLATE_ID_MAX = 64

export interface AskChipView {
  id: string
  text: string
  /** Already asked on this review: the question's page, and whether it has an answer. */
  asked: { url: string; answered: boolean } | null
}

/** "Ask lello75" — the line's button. */
export function askButtonLabel(handle: string): string {
  return `Ask ${handle}`
}

/** "… · answered" / "… · asked" — an asked chip's link text. */
export function askedChipText(text: string, answered: boolean): string {
  return `${text} · ${answered ? 'answered' : 'asked'}`
}

/** The review's product category as a community path, from the line's own taxonomy path, else
 *  its category's. null when neither is under audio. */
function itemCategoryPath(item: CommunityFeedItem): string | null {
  return (
    communityCategoryPath(item.product.full_slug) ??
    communityCategoryPath(item.product.category?.full_slug)
  )
}

/** The bank for a community category path: the longest listed path that is the path itself or a
 *  segment-bounded ancestor of it (`headphones/gaming-headsets` beats `headphones`). */
export function askBankFor(
  path: string | null,
  bank: ReadonlyArray<AskChipCategory> = ASK_CHIP_BANK
): AskChipCategory | null {
  if (!path) {
    return null
  }
  let best: { category: AskChipCategory; length: number } | null = null
  for (const category of bank) {
    for (const p of category.paths) {
      if ((path === p || path.startsWith(`${p}/`)) && (!best || p.length > best.length)) {
        best = { category, length: p.length }
      }
    }
  }
  return best?.category ?? null
}

/** The item's `asked` rows by template id, malformed rows (or a link off this origin) dropped. */
function askedById(
  asked: CommunityFeedItem['asked'],
  trailingSlash: boolean
): Map<string, { url: string; answered: boolean }> {
  const out = new Map<string, { url: string; answered: boolean }>()
  if (!Array.isArray(asked)) {
    return out
  }
  for (const row of asked as Array<Partial<CommunityAskedChip> | null>) {
    const url = rootHref(row?.url)
    if (row && typeof row.template_id === 'string' && url && !out.has(row.template_id)) {
      out.set(row.template_id, {
        url: withSlash(url, trailingSlash),
        answered: row.answered === true,
      })
    }
  }
  return out
}

/**
 * The chips a review offers, rules (a)–(c) applied. `mine` = the chips this browser asked since
 * the page loaded (client-only, never in the HTML): id → the new question's page; DealHunt's
 * `asked` row wins when both exist (it knows whether there is an answer).
 */
export function askChips(
  item: CommunityFeedItem,
  trailingSlash: boolean,
  mine?: ReadonlyMap<string, string>,
  bank: ReadonlyArray<AskChipCategory> = ASK_CHIP_BANK
): AskChipView[] {
  const category = askBankFor(itemCategoryPath(item), bank)
  if (!category) {
    return []
  }
  const answeredReads = new Set(readChips(item).map(chip => chip.id))
  const asked = askedById(item.asked, trailingSlash)
  return category.chips
    .filter((chip: AskChip) => !chip.read || !answeredReads.has(chip.read))
    .slice(0, ASK_CHIPS_SHOWN)
    .map(chip => {
      const theirs = asked.get(chip.id)
      const own = mine?.get(chip.id)
      return {
        id: chip.id,
        text: chip.text,
        asked: theirs ?? (own ? { url: own, answered: false } : null),
      }
    })
}

/** A typed question as DealHunt's one-line title: whitespace collapsed, trimmed. */
export function askTitle(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim()
}

export function askTitleReady(raw: string): boolean {
  const length = Array.from(askTitle(raw)).length
  return length >= ASK_TITLE_MIN && length <= ASK_TITLE_MAX
}

/**
 * The chip whose unedited text the field holds (one-line compare, `askTitle` on both sides), still
 * unasked: its id posts as `template_id`, and it shows as selected. null = a typed question —
 * edited chip text included. Derived from the field on every render, so an edit unselects it.
 */
export function askTemplateFor(
  text: string,
  chips: ReadonlyArray<Pick<AskChipView, 'id' | 'text' | 'asked'>>
): string | null {
  const title = askTitle(text)
  if (!title) {
    return null
  }
  return chips.find(chip => !chip.asked && askTitle(chip.text) === title)?.id ?? null
}

/** The status line after a signed-in reader's question posted. */
export function askSentLine(handle: string, productName: string): string {
  return `Asked — ${handle} and other owners of ${productName} will be notified. We'll email you when one answers.`
}

/** The status line once a signed-out asker's sign-in claimed the question (#209). */
export function askClaimedLine(handle: string): string {
  return `Signed in — we'll email you when ${handle} or another owner answers.`
}

/**
 * The question "Ask" posts (discussions contract §4, owner-notes contract #205): a title-only
 * `question` on the reviewed product; from a chip, its id as `template_id` and the review as
 * `note_id` (#205, #208 — DealHunt takes `note_id` only beside a template id) when the field holds
 * a chip's unedited text (`askTemplateFor`); typed or edited text has neither.
 */
export function askBody(
  item: Pick<CommunityFeedItem, 'note' | 'product'>,
  title: string,
  templateId: string | null
): DiscussionCreateBody {
  return {
    kind: 'question',
    title: askTitle(title),
    blocks: [],
    gear: [{ kind: 'product', node_id: item.product.node_id }],
    // DealHunt takes the review only beside a chip id (#208): a typed question has neither.
    ...(templateId ? { template_id: templateId, note_id: item.note.id } : {}),
  }
}
